import {lazy} from 'react';

import {Navigate, RouterProvider, createBrowserRouter} from 'react-router-dom';

import Index from './Index';

import ErrorDisplayBoundary from 'common/error-display-boundary/ErrorDisplayBoundary';
import NoMatch from 'common/router/no-match/NoMatch';
import Suspend from 'common/suspend/Suspend';
import PPUIProvider from 'patient/ppui/PPUIProvider';
import {useUserStore} from 'user/store';

type EnabledFeatures = {
    documentTypeManagement: boolean;
    eFaxing: boolean;
    integrationMonitoring: boolean;
    supportsPatientRiskProfile: boolean;
};

const Admin = lazy(() => import('poc/admin/Admin'));
const CareDocumentation = lazy(
    () => import('care-documentation/CareDocumentation'),
);
const CurrentRisk = lazy(() => import('current-risk/CurrentRisk'));
const DocumentType = lazy(() => import('document-types/DocumentType'));
const PendingReview = lazy(
    () => import('encounters/pending-review/PendingReview'),
);
const InProgress = lazy(() => import('encounters/in-progress/InProgress'));
const SuggestedChanges = lazy(
    () => import('encounters/suggested-changes/SuggestedChanges'),
);
const FaxOutbox = lazy(() => import('fax-outbox/FaxOutbox'));
const FeeForService = lazy(() => import('fee-for-service/FeeForService'));
const HRAFaxPopup = lazy(
    () => import('patient/ppui/care-planning-app/components/HRA/HRAEfaxPopup'),
);
const IntegrationMonitoring = lazy(
    () => import('integration-monitoring/IntegrationMonitoring'),
);
const Members = lazy(() => import('members/Members'));
const Patient = lazy(() => import('patient/Patient'));
const PatientManagement = lazy(
    () => import('patient-management/PatientManagement'),
);
const Reporting = lazy(() => import('reporting/Reporting'));
const InboundEvent = lazy(
    () => import('integration-monitoring/inbound-event/InboundEvent'),
);
const Settings = lazy(() => import('settings/Settings'));

function createRouter({
    eFaxing,
    integrationMonitoring,
    documentTypeManagement,
    supportsPatientRiskProfile,
}: EnabledFeatures) {
    return createBrowserRouter([
        /**
         * This route is for the HRA eFax popup.
         *
         * It's separate so that it can be opened in a new window without
         * any other UI.
         */
        {
            element: (
                <Suspend
                    child={
                        <PPUIProvider>
                            <HRAFaxPopup />
                        </PPUIProvider>
                    }
                />
            ),
            path: 'patient/:username/fax',
        },
        {
            element: <Index />,
            errorElement: <ErrorDisplayBoundary />,
            path: '/',

            children: [
                {
                    element: <Navigate replace to="/reporting" />,
                    index: true,
                },
                {
                    element: <Navigate replace to="/members" />,
                    path: 'census/*',
                },
                {
                    element: <Suspend child={<Members />} />,
                    path: 'members/*',
                },
                supportsPatientRiskProfile && {
                    element: <Suspend child={<CurrentRisk />} />,
                    path: 'current-risk',
                },
                {
                    element: <Suspend child={<PatientManagement />} />,
                    path: 'patient-management/*',
                },
                {
                    element: <Navigate to="pending-review/" />,
                    path: 'encounters',
                },
                {
                    element: <Suspend child={<PendingReview />} />,
                    path: 'encounters/pending-review',
                },
                {
                    element: <Suspend child={<InProgress />} />,
                    path: 'encounters/in-progress-encounters',
                },
                {
                    element: <Suspend child={<SuggestedChanges />} />,
                    path: 'encounters/suggested-changes',
                },
                {
                    element: <Suspend child={<FeeForService />} />,
                    path: 'fee-for-service/*',
                },
                {
                    element: <Suspend child={<Reporting />} />,
                    path: 'reporting/*',
                },
                {
                    element: <Suspend child={<Patient />} />,
                    path: 'patient/*',
                },
                documentTypeManagement && {
                    element: <Suspend child={<DocumentType />} />,
                    path: 'document-types/*',
                },
                {
                    element: <Suspend child={<CareDocumentation />} />,
                    path: 'care-documentation/',
                },
                eFaxing && {
                    element: <Suspend child={<FaxOutbox />} />,
                    path: 'fax-outbox/*',
                },
                integrationMonitoring && {
                    element: <Suspend child={<InboundEvent />} />,
                    path: 'integration-monitoring/inbound-event/:eventId',
                },
                integrationMonitoring && {
                    element: <Suspend child={<IntegrationMonitoring />} />,
                    path: 'integration-monitoring/*',
                },
                {
                    element: <Suspend child={<Settings />} />,
                    path: 'settings/*',
                },
                {
                    element: <Suspend child={<Admin />} />,
                    path: 'poc/admin',
                },
                {
                    element: <NoMatch />,
                    path: '*',
                },
            ].filter(Boolean),
        },
    ]);
}

const Router = () => {
    const hasIntegrationMonitoring = useUserStore(
        state => state.hasIntegrationMonitoring,
    );
    const {features} = useUserStore(state => state.activeOrg);
    const isDocumentTypeManager = useUserStore(
        state => state.isDocumentTypeManager,
    );

    return (
        <RouterProvider
            router={createRouter({
                documentTypeManagement: isDocumentTypeManager,
                eFaxing: features?.eFaxing ?? false,
                integrationMonitoring: hasIntegrationMonitoring,
                supportsPatientRiskProfile:
                    features?.supportsPatientRiskProfile,
            })}
        />
    );
};

export default Router;
